import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Tabs, Tab, Container, Grid, Typography } from "@mui/material";
import {
  areObjectsEqual,
  findGetParameter,
  getData,
  newPath
} from "../../components/utilities/UtilityFunctions";

import { postNotification } from "../../components/utilities/Notifications";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";

import OrganizationDetails from "./components/OrganizationDetails";
import OrganizationUsers from "./components/OrganizationUsers";
import OrganizationCircuits from "./components/OrganizationCircuits";
import OrganizationWidgets from "./components/OrganizationWidgets";
import { handlePagingAll } from "../../components/api/APIUtilities";
import OrganizationBanking from "./components/OrganizationBanking";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import Redirect from "../../components/utilities/Redirect";
import OrganizationDashboard from "./components/OrganizationDashboard";
import OrganizationHelmet from "./components/subcomponents/OrganizationHelmet";
import OrganizationSubscriptionBlock from "../subscription/components/OrganizationSubscriptionBlock";
import { globalVariables } from "../../global/GlobalVariables";

const IS_UY = getEnvironmentVariable("IS_UY") === "true";

const SYSTEM_IMAGES_CLOUDFRONT = getEnvironmentVariable(
  "SYSTEM_IMAGES_CLOUDFRONT"
);

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`organization-tabpanel-${index}`}
      aria-labelledby={`organization-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </Box>
  );
}

function a11yProps(index) {
  return {
    id: `organization-tab-${index}`,
    "aria-controls": `organization-tabpanel-${index}`
  };
}

var tabIndex = -1;
var tabIndexes = {};
var tabsArray = [];

/*
TODO: Indexes will need to be updated once "subscriptions" tab is added to BCP

Tab Indexes
--------------
Base / UYS
0 - Overview
1 - Details
2 - Banking
3 - Subscriptions
4 - Users
5 - Circuits
/ - Ticket Sales
6 - Widgets
*/

tabIndex++;
tabIndexes["overview"] = tabIndex;
tabsArray.push({
  index: tabIndex,
  label: "Overview",
  key: "overview"
});

tabIndex++;
tabIndexes["details"] = tabIndex;
tabsArray.push({
  index: tabIndex,
  label: "Details",
  key: "details"
});

tabIndex++;
tabIndexes["banking"] = tabIndex;
tabsArray.push({
  index: tabIndex,
  label: "Banking",
  key: "banking"
});

tabIndex++;
tabIndexes["subscriptions"] = tabIndex;
tabsArray.push({
  index: tabIndex,
  label: "Subscriptions",
  key: "subscriptions"
});

tabIndex++;
tabIndexes["users"] = tabIndex;
tabsArray.push({
  index: tabIndex,
  label: "Users",
  key: "users"
});

tabIndex++;
tabIndexes["circuits"] = tabIndex;
tabsArray.push({
  index: tabIndex,
  label: "Circuits",
  key: "circuits"
});

// tabIndex++;
// tabIndexes["tickets"] = tabIndex;
// tabsArray.push({
//   index: tabIndex,
//   label: "Ticket Sales",
//   key: "tickets",
// });

tabIndex++;
tabIndexes["widgets"] = tabIndex;
tabsArray.push({
  index: tabIndex,
  label: "Widgets",
  key: "widgets"
});

export async function getOrganization(organizationId, authContext, options) {
  var apiGetData = await getData(`/gamestores/${organizationId}`, {
    queryStringParameters: {
      "expand[]": ["location", "owner", "bankAccount"]
    }
  });

  if (!apiGetData.socials) {
    apiGetData.socials = {};
  } else if (Array.isArray(apiGetData.socials)) {
    // handle legacy data
    var socials = {};
    apiGetData.socials.forEach((social) => {
      socials[social.type] = social.username;
    });
    apiGetData.socials = socials;
  }

  var storeUser = await handlePagingAll(`/gamestoreusers`, {
    limit: 100,
    gameStoreId: organizationId,
    userId: authContext?.authState?.identity?.userId
  });
  apiGetData.myStoreUser = storeUser?.length > 0 ? storeUser[0] : null;

  const fetchFile = async (fileId) => {
    if (!fileId) return;

    var returnObj;
    try {
      returnObj = await getData(`/files/${fileId}`, {
        queryStringParameters: {}
      });
    } catch (err) {
      console.error(err);
    }
    return returnObj;
  };

  const fetchSubs = async () => {
    let returnObj;

    try {
      returnObj = await handlePagingAll(`/subscriptions`, {
        gameStoreId: organizationId,
        subscriptionType: "organization"
      });
    } catch (err) {
      console.error(err);
    }

    return returnObj;
  };

  async function parallel() {
    const taskPhoto = options?.photo ? fetchFile(apiGetData.photoFileId) : null;
    const taskSubs = fetchSubs();

    return {
      photo: await taskPhoto,
      subs: await taskSubs
    };
  }

  var parallelData = await parallel();

  apiGetData.photo = parallelData.photo;
  apiGetData.subscriptions = parallelData.subs;

  if (apiGetData?.subscriptions?.length) {
    let activeSub = false;

    apiGetData.subscriptions.forEach((sub) => {
      if (sub?.active) activeSub = true;
    });

    apiGetData.subscribed = activeSub;
  }

  return apiGetData;
}

export default function OrganizationOrganize() {
  let { organizationId } = useParams();
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [organization, setOrganization] = useState(null);
  const [redirect, setRedirect] = useState(null);

  const activeTabParam = findGetParameter("active_tab");

  const [activeTab, setActiveTab] = useState(
    !organizationId
      ? 1
      : activeTabParam
        ? tabIndexes[activeTabParam.toLowerCase()] !== undefined
          ? tabIndexes[activeTabParam.toLowerCase()]
          : 0
        : 0
  );

  const [tabStates, setTabStates] = useState({
    player: "Monthly",
    organization: "Monthly"
  });

  const globalVars = globalVariables();
  const affiliate = findGetParameter("affiliate");

  const handleTabChange = async (e, newTab) => {
    setLoading(true);
    try {
      await hasAccess(organization, authContext, newTab, true);
    } catch (err) {
      setLoading(false);
      return;
    }

    let path;
    Object.keys(tabIndexes).forEach((tabIndex) => {
      if (tabIndexes[tabIndex] === newTab) {
        path = newPath({
          active_tab: tabIndex
        });

        return true;
      }
    });
    setActiveTab(newTab);
    navigate(path, { replace: false });
  };

  useEffect(() => {
    const check = async () => {
      setLoading(true);

      try {
        if (organizationId) {
          const apiStore =
            organization ||
            (await getOrganization(organizationId, authContext, {
              photo: activeTab === 0
            }));

          try {
            await hasAccess(apiStore, authContext, activeTab, true);
          } catch (err) {
            navigate("/organize/organizations");
            return;
          }
          if (!areObjectsEqual(organization, apiStore)) {
            setOrganization(apiStore);
          }
        } else {
          setOrganization({});
        }
      } catch (err) {
        postNotification("error", err);
        setRedirect("/organization/:organizationId");
        return;
      } finally {
        setLoading(false);
      }
    };
    check();
    // eslint-disable-next-line
  }, [activeTab]);

  return (
    <>
      {Boolean(loading) && <ModalLoadingFull />}

      {/* Redirects user to player-facing event page if "redirect" variable is set to a specific path */}
      {redirect && (
        <Redirect
          path={redirect}
          useWindow
          message={
            "You do not have access to this page. If you believe this is a mistake, please contact the organization."
          }
        />
      )}

      {organization && (
        <Box sx={{ width: "100%" }}>
          {organization && <OrganizationHelmet organization={organization} />}

          {organization && (
            <OrganizationTabs
              label={organization.name}
              organization={organization}
              activeTab={activeTab}
              handleTabChange={handleTabChange}
              authContext={authContext}
            />
          )}

          {tabIndexes["overview"] !== undefined && (
            <TabPanel value={activeTab} index={tabIndexes["overview"]}>
              <OrganizationDashboard
                organization={organization}
                setOrganization={setOrganization}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                handleTabChange={handleTabChange}
                authContext={authContext}
                organize
              />
            </TabPanel>
          )}
          {tabIndexes["details"] !== undefined && (
            <TabPanel value={activeTab} index={tabIndexes["details"]}>
              <OrganizationDetails
                organization={organization}
                setOrganization={setOrganization}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                handleTabChange={handleTabChange}
                authContext={authContext}
              />
            </TabPanel>
          )}
          {tabIndexes["banking"] !== undefined && (
            <TabPanel value={activeTab} index={tabIndexes["banking"]}>
              <OrganizationBanking
                organization={organization}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                handleTabChange={handleTabChange}
                authContext={authContext}
              />
            </TabPanel>
          )}
          {tabIndexes["subscriptions"] !== undefined && (
            <TabPanel value={activeTab} index={tabIndexes["subscriptions"]}>
              <OrganizationSubscriptionBlock
                tabStates={tabStates}
                setTabStates={setTabStates}
                user={organization}
                userSubscriptions={{
                  organizationSubscription: {
                    isSubscribed: organization?.subscribed || false,
                    type:
                      organization?.subscriptions?.[0]?.stripe?.plan
                        ?.interval === "year"
                        ? "Yearly"
                        : organization?.subscriptions?.[0]?.stripe?.plan
                              ?.interval === "month"
                          ? "Monthly"
                          : null
                  }
                }}
                globalVars={globalVars}
                affiliate={affiliate}
                props={{}}
                SYSTEM_IMAGES_CLOUDFRONT={SYSTEM_IMAGES_CLOUDFRONT}
              />
            </TabPanel>
          )}
          {tabIndexes["users"] !== undefined && (
            <TabPanel value={activeTab} index={tabIndexes["users"]}>
              <OrganizationUsers
                organization={organization}
                setOrganization={setOrganization}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                handleTabChange={handleTabChange}
                authContext={authContext}
              />
            </TabPanel>
          )}
          {tabIndexes["circuits"] !== undefined && (
            <TabPanel value={activeTab} index={tabIndexes["circuits"]}>
              <OrganizationCircuits
                organization={organization}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                handleTabChange={handleTabChange}
                authContext={authContext}
              />
            </TabPanel>
          )}
          {/* {tabIndexes["tickets"] !== undefined && (
            <TabPanel value={activeTab} index={tabIndexes["tickets"]}>
              <OrganizationTicketSales
                organization={organization}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                handleTabChange={handleTabChange}
                authContext={authContext}
              />
            </TabPanel>
          )} */}
          {tabIndexes["widgets"] !== undefined && (
            <TabPanel value={activeTab} index={tabIndexes["widgets"]}>
              <OrganizationWidgets
                organization={organization}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                handleTabChange={handleTabChange}
                authContext={authContext}
              />
            </TabPanel>
          )}
        </Box>
      )}
    </>
  );
}

export function OrganizationTabs(props) {
  const { organization, activeTab, authContext, handleTabChange } = props;

  return (
    <Box
      sx={{
        maxWidth: "100%",

        backgroundColor: "banner.main",
        color: "banner.contrastText",
        borderBottom: 1,
        borderColor: "divider"
      }}
    >
      <Container maxWidth="lg">
        <Grid container alignItems="center">
          <Grid item xs={12} pr={[2, 0]} pl={[2, 0]}>
            <Typography variant="h3" gutterBottom>
              {getEnvironmentVariable("IS_SOLFORGE") === "true"
                ? "Organized Play Settings"
                : getEnvironmentVariable("IS_UY") === "true"
                  ? "Game Store Settings"
                  : `${organization?.name || "Organization"} Settings`}
            </Typography>
          </Grid>
        </Grid>
        <Box
          sx={{
            width: "100%"
          }}
        >
          {tabsArray?.length > 0 && (
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              aria-label="organization tabs"
            >
              {organization?.id && tabIndexes["overview"] !== undefined && (
                <Tab label={"Profile"} {...a11yProps(tabIndexes["overview"])} />
              )}
              <Tab
                label={"Settings"}
                {...a11yProps(tabIndexes["details"])}
                disabled={
                  !organization?.id
                    ? false
                    : authContext?.authState?.identity?.isAdmin !== true &&
                      !organization?.myStoreUser?.updateStore &&
                      organization?.ownerId !==
                        authContext?.authState?.identity?.userId
                }
              />
              {organization?.id && tabIndexes["banking"] !== undefined && (
                <Tab
                  label="Banking"
                  {...a11yProps(tabIndexes["banking"])}
                  disabled={
                    authContext?.authState?.identity?.isAdmin !== true &&
                    organization?.ownerId !==
                      authContext?.authState?.identity?.userId
                  }
                />
              )}
              {organization?.id &&
                tabIndexes["subscriptions"] !== undefined && (
                  <Tab
                    label="Subscriptions"
                    {...a11yProps(tabIndexes["subscriptions"])}
                    disabled={
                      authContext?.authState?.identity?.isAdmin !== true &&
                      organization?.ownerId !==
                        authContext?.authState?.identity?.userId
                    }
                  />
                )}
              {organization?.id && tabIndexes["users"] !== undefined && (
                <Tab
                  label={"Personnel"}
                  {...a11yProps(tabIndexes["users"])}
                  disabled={
                    authContext?.authState?.identity?.isAdmin !== true &&
                    !organization?.myStoreUser?.manageUsers &&
                    organization?.ownerId !==
                      authContext?.authState?.identity?.userId
                  }
                />
              )}
              {organization?.id && tabIndexes["circuits"] !== undefined && (
                <Tab label="Circuits" {...a11yProps(tabIndexes["circuits"])} />
              )}
              {organization?.id && tabIndexes["tickets"] !== undefined && (
                <Tab
                  label="Ticket Sales"
                  {...a11yProps(tabIndexes["tickets"])}
                  disabled={
                    authContext?.authState?.identity?.isAdmin !== true &&
                    organization?.ownerId !==
                      authContext?.authState?.identity?.userId
                  }
                />
              )}
              {organization?.id && tabIndexes["widgets"] !== undefined && (
                <Tab label={"Embed"} {...a11yProps(tabIndexes["widgets"])} />
              )}
            </Tabs>
          )}
        </Box>
      </Container>
    </Box>
  );
}

// TODO: Will need to update this function once "subscriptions" tab is added to BCP
export async function hasAccess(organization, authContext, tabIndex, notify) {
  var err;

  if (authContext?.authState?.identity?.isAdmin === true) {
    return true;
  }

  if (organization) {
    // Allowed tabs for non-subbed UY gamestores
    if (IS_UY && !organization?.subscribed) {
      if (authContext?.authState?.identity?.userId === organization?.ownerId) {
        // Overview / Org Details / Banking / Subscriptions
        if (
          tabIndexes["overview"] === tabIndex ||
          tabIndexes["details"] === tabIndex ||
          tabIndexes["banking"] === tabIndex ||
          tabIndexes["subscriptions"] === tabIndex
        ) {
          return true;
        }
      }

      if (organization.myStoreUser) {
        // Org Overview / Org Details
        if (
          tabIndexes["overview"] === tabIndex ||
          tabIndexes["details"] === tabIndex
        ) {
          if (organization.myStoreUser.updateStore) {
            return true;
          }
        }
      }

      err = new Error(
        "Game store must have an active subscription to access to this feature."
      );
    } else {
      if (authContext?.authState?.identity?.userId === organization.ownerId) {
        return true;
      }

      if (organization.myStoreUser) {
        // Org overiew
        if (tabIndexes["overview"] === tabIndex) {
          return true;
        }

        // Org Details
        if (tabIndexes["details"] === tabIndex) {
          if (organization.myStoreUser.updateStore) {
            return true;
          }
        }

        //  Circuits
        if (tabIndexes["circuits"] === tabIndex) {
          if (
            organization.myStoreUser.createLeagues ||
            organization.myStoreUser.approveTokens ||
            organization.myStoreUser.corrections
          ) {
            return true;
          }
        }

        // Manage users
        if (tabIndexes["users"] === tabIndex) {
          if (organization.myStoreUser.manageUsers) {
            return true;
          }
        }

        // Circuits
        if (tabIndex === 5) {
          if (
            organization.myStoreUser.approveTokens ||
            organization.myStoreUser.corrections ||
            organization.myStoreUser.createLeagues
          ) {
            return true;
          }
        }

        // Widgets
        if (tabIndexes["widgets"] === tabIndex) {
          return true;
        }
      }
    }
  } else {
    return true;
  }

  if (!err)
    err = new Error(
      "You do not have access to this function for this Organization"
    );

  if (notify) postNotification("warning", err);

  if (err) {
    throw err;
  }
  return false;
}
